import { Controller } from "@hotwired/stimulus"
import {
  formatEta,
  percentForStageIndex,
  progressPercentAt,
  stageIndexAt,
  stageTimings
} from "../helpers/analysis_progress_estimate"

// Estimated progress for RAG detailed analysis waiting UI.
// Stage timings follow the multi-step pipeline (~8 min typical); the bar never
// reaches 100% until Turbo replaces the frame. Elapsed time is persisted in
// sessionStorage so a frame reload does not restart the estimate.
export default class extends Controller {
  static targets = ["bar", "label", "detail", "counter", "eta", "stage"]

  static values = {
    stages: { type: Array, default: [] },
    durationMs: { type: Number, default: 480000 },
    maxPercent: { type: Number, default: 92 },
    reducedMotionPercent: { type: Number, default: 35 },
    eta: { type: Object, default: {} },
    storageKey: { type: String, default: "" },
    stepTemplate: { type: String, default: "" },
    serverStage: { type: Number, default: -1 }
  }

  connect() {
    this.startedAt = this.readStartedAt()
    this.timings = stageTimings(this.stagesValue, this.durationMsValue)
    this.rafId = null
    this.lastStageIndex = -1
    this.lastEtaText = ""

    if (this.prefersReducedMotion()) {
      const serverIndex = Math.max(0, this.serverStageValue)
      const serverPercent = percentForStageIndex(this.serverStageValue, this.stagesValue, this.maxPercentValue)
      this.setProgress(Math.max(this.reducedMotionPercentValue, serverPercent))
      this.applyStage(serverIndex)
      this.setEta(0)
      return
    }

    this.tick = this.tick.bind(this)
    this.rafId = window.requestAnimationFrame(this.tick)
  }

  disconnect() {
    if (this.rafId != null) {
      window.cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
  }

  tick() {
    const elapsed = Date.now() - this.startedAt
    const timerPercent = progressPercentAt(elapsed, this.durationMsValue, this.maxPercentValue)
    const serverPercent = percentForStageIndex(this.serverStageValue, this.stagesValue, this.maxPercentValue)
    this.setProgress(Math.max(timerPercent, serverPercent))
    const timerIndex = stageIndexAt(elapsed, this.timings)
    const serverIndex = this.serverStageValue
    this.applyStage(Math.max(timerIndex, serverIndex))
    this.setEta(elapsed)
    this.rafId = window.requestAnimationFrame(this.tick)
  }

  setProgress(value) {
    if (!this.hasBarTarget) return

    const rounded = Math.round(value)
    this.barTarget.value = rounded
    this.barTarget.textContent = `${rounded}%`
  }

  applyStage(index) {
    if (index === this.lastStageIndex) return
    this.lastStageIndex = index

    const stage = this.timings[index]
    if (!stage) return

    if (this.hasLabelTarget && this.labelTarget.textContent !== stage.title) {
      this.labelTarget.textContent = stage.title
    }
    if (this.hasDetailTarget && this.detailTarget.textContent !== stage.detail) {
      this.detailTarget.textContent = stage.detail
    }
    if (this.hasCounterTarget && this.stepTemplateValue) {
      this.counterTarget.textContent = this.stepTemplateValue
        .replace("{current}", String(index + 1))
        .replace("{total}", String(this.timings.length))
    }

    this.stageTargets.forEach((item, i) => {
      item.classList.toggle("is-current", i === index)
      item.classList.toggle("is-done", i < index)
      item.classList.toggle("is-upcoming", i > index)
      if (i === index) {
        item.setAttribute("aria-current", "step")
      } else {
        item.removeAttribute("aria-current")
      }
    })
  }

  setEta(elapsedMs) {
    if (!this.hasEtaTarget) return

    const text = formatEta(elapsedMs, this.durationMsValue, this.etaValue)
    if (!text || text === this.lastEtaText) return

    this.lastEtaText = text
    this.etaTarget.textContent = text
  }

  readStartedAt() {
    const key = this.storageKeyValue
    const now = Date.now()
    if (!key) return now

    try {
      const stored = Number(window.sessionStorage.getItem(key))
      const maxAgeMs = 2 * 60 * 60 * 1000
      if (Number.isFinite(stored) && stored > 0 && now - stored < maxAgeMs) {
        return stored
      }
      window.sessionStorage.setItem(key, String(now))
    } catch {
      return now
    }

    return now
  }

  prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }
}
